import React, { useEffect, useMemo, useState } from "react";

const REPORTS = [
  { id: "signup", label: "Guest Signup Status" },
  { id: "most_requested", label: "Most Requested Activities" },
  { id: "unfulfilled", label: "Activities Not Able To Be Fulfilled" },
  { id: "over_capacity", label: "Requested Over Capacity" },
  { id: "opted_out", label: "Guests Opted Out of Activities" },
  { id: "builder", label: "Custom Report Builder" },
];

export default function ReportsPage() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [data, setData] = useState(null);
  const [rawLoading, setRawLoading] = useState(false);
  const [rawError, setRawError] = useState("");
  const [raw, setRaw] = useState(null);
  const [reportId, setReportId] = useState("signup");
  const [minRequests, setMinRequests] = useState(1);
  const [onlyUnfulfilled, setOnlyUnfulfilled] = useState(false);
  const [selectedActivityId, setSelectedActivityId] = useState("");
  const [selectedGuestId, setSelectedGuestId] = useState("");
  const [mostRequestedSort, setMostRequestedSort] = useState({ key: "count", dir: "desc" });
  const [builderSource, setBuilderSource] = useState("guests");
  const [builderSearch, setBuilderSearch] = useState("");
  const [builderFrom, setBuilderFrom] = useState("");
  const [builderTo, setBuilderTo] = useState("");
  const [builderDatePreset, setBuilderDatePreset] = useState("this_week");
  const [builderHasRequests, setBuilderHasRequests] = useState(false);
  const [builderMinRequested, setBuilderMinRequested] = useState(0);
  const [builderColumns, setBuilderColumns] = useState([]);
  const [builderGroupBy, setBuilderGroupBy] = useState("");
  const [builderAggregate, setBuilderAggregate] = useState("count");
  const [builderSumField, setBuilderSumField] = useState("");

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      setError("");
      try {
        const res = await fetch("/api/reports/summary");
        const json = await res.json();
        if (!res.ok) {
          setError(json?.error || json?.details || "Failed to load reports");
          return;
        }
        if (!cancelled) setData(json);
      } catch (e) {
        if (!cancelled) setError("Failed to load reports");
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    async function loadRaw() {
      setRawLoading(true);
      setRawError("");
      try {
        const res = await fetch("/api/reports/raw");
        const json = await res.json();
        if (!res.ok) {
          setRawError(json?.error || json?.details || "Failed to load raw data");
          return;
        }
        if (!cancelled) setRaw(json);
      } catch (e) {
        if (!cancelled) setRawError("Failed to load raw data");
      } finally {
        if (!cancelled) setRawLoading(false);
      }
    }
    loadRaw();
    return () => {
      cancelled = true;
    };
  }, []);

  const familyNameMap = useMemo(() => {
    const map = new Map();
    (raw?.families || []).forEach((f) => map.set(f.id, f.name || ""));
    return map;
  }, [raw]);

  const familySizeMap = useMemo(() => {
    const counts = new Map();
    (raw?.guests || []).forEach((g) => {
      (g.familyIds || []).forEach((id) => {
        counts.set(id, (counts.get(id) || 0) + 1);
      });
    });
    return counts;
  }, [raw]);

  const guestNameMap = useMemo(() => {
    const map = new Map();
    (raw?.guests || []).forEach((g) => map.set(g.id, g.name || ""));
    return map;
  }, [raw]);

  const activityNameMap = useMemo(() => {
    const map = new Map();
    (raw?.activities || []).forEach((a) => map.set(a.id, a.name || ""));
    return map;
  }, [raw]);

  const familyMap = useMemo(() => {
    const map = new Map();
    (raw?.families || []).forEach((f) => map.set(f.id, f));
    return map;
  }, [raw]);

  const rawGuestMap = useMemo(() => {
    const map = new Map();
    (raw?.guests || []).forEach((g) => map.set(g.id, g));
    return map;
  }, [raw]);

  const activityStats = useMemo(() => {
    if (!raw) return [];
    const requested = new Map();
    (raw.guests || []).forEach((g) => {
      (g.choices || []).filter(Boolean).forEach((id) => {
        requested.set(id, (requested.get(id) || 0) + 1);
      });
    });
    const scheduledGuests = new Map();
    const capacity = new Map();
    (raw.schedules || []).forEach((s) => {
      (s.activityIds || []).forEach((id) => {
        if (!scheduledGuests.has(id)) scheduledGuests.set(id, new Set());
        s.guestIds.forEach((gid) => scheduledGuests.get(id).add(gid));
        capacity.set(id, (capacity.get(id) || 0) + (s.spaces || 0));
      });
    });
    return (raw.activities || []).map((a) => {
      const scheduled = (scheduledGuests.get(a.id) || new Set()).size;
      const cap = capacity.get(a.id) || 0;
      const remaining = Math.max(0, cap - scheduled);
      return {
        id: a.id,
        name: a.name,
        requested: requested.get(a.id) || 0,
        scheduled,
        capacity: cap,
        remaining,
      };
    });
  }, [raw]);

  const familyStats = useMemo(() => {
    if (!raw) return [];
    const counts = new Map();
    (raw.guests || []).forEach((g) => {
      (g.familyIds || []).forEach((id) => {
        counts.set(id, (counts.get(id) || 0) + 1);
      });
    });
    return (raw.families || []).map((f) => ({
      id: f.id,
      name: f.name,
      guestCount: counts.get(f.id) || 0,
    }));
  }, [raw]);

  const schemaBySource = useMemo(
    () => ({
      guests: [
        { key: "name", label: "Name" },
        { key: "primaryFamily", label: "Primary Family" },
        { key: "arrivalDate", label: "Arrival" },
        { key: "departureDate", label: "Departure" },
        { key: "requested", label: "Requests", numeric: true },
        { key: "families", label: "Families" },
        { key: "activities", label: "Requested Activities" },
        { key: "scheduledActivities", label: "Scheduled Activities" },
        { key: "schedules", label: "Schedules" },
        { key: "email", label: "Email" },
        { key: "phone", label: "Phone" },
        { key: "level", label: "Level" },
      ],
      activities: [
        { key: "name", label: "Activity" },
        { key: "requested", label: "Requested", numeric: true },
        { key: "scheduled", label: "Scheduled", numeric: true },
        { key: "capacity", label: "Capacity", numeric: true },
        { key: "remaining", label: "Remaining", numeric: true },
        { key: "scheduledGuests", label: "Guests" },
        { key: "scheduledDates", label: "Schedules" },
      ],
      schedules: [
        { key: "date", label: "Date" },
        { key: "slot", label: "Slot" },
        { key: "activities", label: "Activities" },
        { key: "guestNames", label: "Guest Names" },
        { key: "spaces", label: "Spaces", numeric: true },
        { key: "guestCount", label: "Guests", numeric: true },
      ],
      families: [],
    }),
    []
  );

  useEffect(() => {
    const defaults = {
      guests: ["name", "arrivalDate", "departureDate", "requested", "families", "activities", "scheduledActivities", "schedules"],
      activities: ["name", "requested", "scheduled", "capacity", "remaining", "scheduledGuests"],
      schedules: ["date", "slot", "activities", "guestNames", "spaces", "guestCount"],
      families: [],
    };
    setBuilderColumns(defaults[builderSource]);
    setBuilderGroupBy("");
    setBuilderAggregate("count");
    setBuilderSumField("");
  }, [builderSource]);

  useEffect(() => {
    const today = new Date();
    const day = new Date(today.getFullYear(), today.getMonth(), today.getDate());
    const pad = (n) => String(n).padStart(2, "0");
    const toYMD = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
    function setRange(start, end) {
      setBuilderFrom(toYMD(start));
      setBuilderTo(toYMD(end));
    }
    if (builderDatePreset === "today") {
      setRange(day, day);
    } else if (builderDatePreset === "tomorrow") {
      const t = new Date(day);
      t.setDate(t.getDate() + 1);
      setRange(t, t);
    } else if (builderDatePreset === "hansen_wedding") {
      setRange(new Date(2026, 2, 26), new Date(2026, 2, 30));
    } else if (builderDatePreset === "this_week") {
      const start = new Date(day);
      start.setDate(start.getDate() - start.getDay());
      const end = new Date(start);
      end.setDate(end.getDate() + 6);
      setRange(start, end);
    } else if (builderDatePreset === "next_week") {
      const start = new Date(day);
      start.setDate(start.getDate() - start.getDay() + 7);
      const end = new Date(start);
      end.setDate(end.getDate() + 6);
      setRange(start, end);
    }
  }, [builderDatePreset]);

  const builderRows = useMemo(() => {
    if (!raw) return [];
    const q = builderSearch.trim().toLowerCase();
    const scheduleInRange = (date) => {
      if (!builderFrom && !builderTo) return true;
      if (!date) return false;
      if (builderFrom && date < builderFrom) return false;
      if (builderTo && date > builderTo) return false;
      return true;
    };
    const schedulesInRange = (raw.schedules || []).filter((s) => scheduleInRange(s.date));
    const schedulesByGuest = new Map();
    const schedulesByActivity = new Map();
    schedulesInRange.forEach((s) => {
      (s.guestIds || []).forEach((gid) => {
        if (!schedulesByGuest.has(gid)) schedulesByGuest.set(gid, []);
        schedulesByGuest.get(gid).push(s);
      });
      (s.activityIds || []).forEach((aid) => {
        if (!schedulesByActivity.has(aid)) schedulesByActivity.set(aid, []);
        schedulesByActivity.get(aid).push(s);
      });
    });
    const scheduleLabel = (s) => `${s.date || ""} ${s.slot || ""}`.trim();
    const activitiesByGuest = new Map();
    (raw.guests || []).forEach((g) => {
      const ids = (g.choices || []).filter(Boolean);
      activitiesByGuest.set(g.id, ids.map((id) => activityNameMap.get(id) || "Unknown").join(", "));
    });
    const scheduledActivitiesByGuest = new Map();
    (raw.guests || []).forEach((g) => {
      const scheds = schedulesByGuest.get(g.id) || [];
      const activityIds = new Set();
      scheds.forEach((s) => (s.activityIds || []).forEach((id) => activityIds.add(id)));
      scheduledActivitiesByGuest.set(
        g.id,
        Array.from(activityIds).map((id) => activityNameMap.get(id) || "Unknown").join(", ")
      );
    });
    if (builderSource === "guests") {
      return (raw.guests || [])
        .map((g) => ({
          id: g.id,
          name: g.name,
          primaryFamily: (g.familyIds || [])
            .map((id) => ({ id, name: familyNameMap.get(id) || "", size: familySizeMap.get(id) || 0 }))
            .sort((a, b) => (b.size !== a.size ? b.size - a.size : a.name.localeCompare(b.name)))[0]?.name || "",
          arrivalDate: g.arrivalDate,
          departureDate: g.departureDate,
          requested: (g.choices || []).filter(Boolean).length,
          families: (g.familyIds || []).map((id) => familyNameMap.get(id)).filter(Boolean).join(", "),
          activities: activitiesByGuest.get(g.id) || "",
          scheduledActivities: scheduledActivitiesByGuest.get(g.id) || "",
          schedules: (schedulesByGuest.get(g.id) || []).map(scheduleLabel).join(", "),
          email: g.email,
          phone: g.phone,
          level: g.level,
        }))
        .filter((g) => !q || (g.name || "").toLowerCase().includes(q))
        .filter((g) => {
          if (!builderHasRequests) return true;
          return g.requested >= builderMinRequested;
        })
        .filter((g) => {
          if (!builderFrom && !builderTo) return true;
          if (!g.arrivalDate || !g.departureDate) return false;
          if (builderFrom && g.departureDate <= builderFrom) return false;
          if (builderTo && g.arrivalDate > builderTo) return false;
          return true;
        });
    }
    if (builderSource === "activities") {
      return activityStats
        .filter((a) => !q || (a.name || "").toLowerCase().includes(q))
        .filter((a) => a.requested >= builderMinRequested)
        .filter((a) => {
          if (!builderFrom && !builderTo) return true;
          return (schedulesByActivity.get(a.id) || []).length > 0;
        })
        .map((a) => {
          const scheds = schedulesByActivity.get(a.id) || [];
          const guestIds = new Set();
          scheds.forEach((s) => (s.guestIds || []).forEach((id) => guestIds.add(id)));
          return {
            ...a,
            scheduledGuests: Array.from(guestIds).map((id) => guestNameMap.get(id) || "Unknown").join(", "),
            scheduledDates: scheds.map(scheduleLabel).join(", "),
          };
        });
    }
    if (builderSource === "schedules") {
      return (raw.schedules || [])
        .map((s) => ({
          id: s.id,
          date: s.date,
          slot: s.slot,
          activities: (s.activityIds || []).map((id) => activityNameMap.get(id) || "Unknown").join(" / "),
          guestNames: (s.guestIds || []).map((id) => guestNameMap.get(id) || "Unknown").join(", "),
          spaces: s.spaces || 0,
          guestCount: (s.guestIds || []).length,
        }))
        .filter((s) => {
          if (builderFrom && s.date < builderFrom) return false;
          if (builderTo && s.date > builderTo) return false;
          return true;
        })
        .filter((s) => {
          if (!q) return true;
          const hay = `${s.activities} ${s.guestNames}`.toLowerCase();
          return hay.includes(q);
        });
    }
    if (builderSource === "families") {
      return familyStats;
    }
    return [];
  }, [
    raw,
    builderSource,
    builderSearch,
    builderFrom,
    builderTo,
    builderHasRequests,
    builderMinRequested,
    familyNameMap,
    familySizeMap,
    guestNameMap,
    activityNameMap,
    activityStats,
    familyStats,
  ]);

  const builderOutput = useMemo(() => {
    if (!builderGroupBy) return builderRows;
    const groups = new Map();
    builderRows.forEach((row) => {
      const key = row[builderGroupBy] || "(Blank)";
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key).push(row);
    });
    return Array.from(groups.entries()).map(([key, rows]) => {
      const base = { group: key, count: rows.length };
      if (builderAggregate === "sum" && builderSumField) {
        const sum = rows.reduce((acc, r) => acc + Number(r[builderSumField] || 0), 0);
        base.sum = sum;
      }
      return base;
    });
  }, [builderRows, builderGroupBy, builderAggregate, builderSumField]);

  const activityRows = useMemo(() => {
    const list = data?.activityRequests || [];
    const filtered = list
      .filter((row) => row.count >= minRequests)
      .filter((row) => (onlyUnfulfilled ? row.fulfilled === false : true));
    const sorted = [...filtered].sort((a, b) => {
      const dir = mostRequestedSort.dir === "asc" ? 1 : -1;
      if (mostRequestedSort.key === "avgRank") {
        const av = a.avgRank ?? 9999;
        const bv = b.avgRank ?? 9999;
        if (av !== bv) return (av - bv) * dir;
      } else {
        if (a.count !== b.count) return (a.count - b.count) * dir;
      }
      return (a.name || "").localeCompare(b.name || "");
    });
    return sorted;
  }, [data, minRequests, onlyUnfulfilled, mostRequestedSort]);

  const overCapacityRows = useMemo(() => {
    const list = data?.overCapacity || [];
    return list.filter((row) => row.requested >= minRequests);
  }, [data, minRequests]);

  const optedOutRows = useMemo(() => {
    if (!raw) return [];
    return (raw.guests || [])
      .filter((g) => (g.skipSelections || []).some(Boolean))
      .map((g) => ({
        id: g.id,
        name: g.name || "",
        familyIds: g.familyIds || [],
        skipSelections: g.skipSelections || [false, false, false],
      }))
      .sort((a, b) => {
        const aIdx = a.skipSelections.findIndex(Boolean);
        const bIdx = b.skipSelections.findIndex(Boolean);
        const aKey = aIdx === -1 ? 99 : aIdx;
        const bKey = bIdx === -1 ? 99 : bIdx;
        if (aKey !== bKey) return aKey - bKey;
        return (a.name || "").localeCompare(b.name || "");
      });
  }, [raw]);

  const selectedActivity = useMemo(() => {
    if (!selectedActivityId) return null;
    return data?.activityDetails?.[selectedActivityId] || null;
  }, [data, selectedActivityId]);

  const escapeCsv = (value) => {
    const text = value == null ? "" : String(value);
    if (/[",\n]/.test(text)) {
      return `"${text.replace(/"/g, '""')}"`;
    }
    return text;
  };

  const downloadCsv = (filename, headers, rows) => {
    const headerLine = headers.map(escapeCsv).join(",");
    const lines = rows.map((row) => row.map(escapeCsv).join(","));
    const csv = [headerLine, ...lines].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const exportSignupCsv = () => {
    const signed = data?.signup?.signedUpGuests || [];
    const notSigned = data?.signup?.notSignedUpGuests || [];
    const choiceHeaders = Array.from({ length: 10 }, (_, i) => `Choice ${i + 1}`);
    const headers = [
      "Status",
      "Guest",
      "Requested Count",
      "Primary Family",
      "Primary Family Size",
      "Family Count",
      ...choiceHeaders,
      "Skip Activity 1",
      "Skip Activity 2",
      "Skip Activity 3",
    ];
    const resolveSkipLabel = (familyIds, index) => {
      for (const id of familyIds || []) {
        const fam = familyMap.get(id);
        if (!fam) continue;
        const text = fam[`skipText${index + 1}`] || "";
        if (text) return text;
        const date = fam[`skipDate${index + 1}`] || "";
        const slot = fam[`skipSlot${index + 1}`] || "";
        if (date || slot) return [date, slot].filter(Boolean).join(" ");
      }
      return "";
    };
    const buildRow = (g, status) => {
      const rawGuest = rawGuestMap.get(g.id);
      const choices = rawGuest?.choices || [];
      const choiceNames = choiceHeaders.map((_, idx) => {
        const id = choices[idx] || "";
        return id ? activityNameMap.get(id) || "" : "";
      });
      const skipSelections = rawGuest?.skipSelections || [];
      const skipLabels = [0, 1, 2].map((idx) => resolveSkipLabel(rawGuest?.familyIds || [], idx));
      return [
        status,
        g.name || "",
        g.requestedCount ?? 0,
        g.primaryFamilyName || "",
        g.primaryFamilySize ?? 0,
        g.families?.length ?? 0,
        ...choiceNames,
        skipSelections[0] ? skipLabels[0] || "Yes" : "",
        skipSelections[1] ? skipLabels[1] || "Yes" : "",
        skipSelections[2] ? skipLabels[2] || "Yes" : "",
      ];
    };
    const rows = [
      ...signed.map((g) => buildRow(g, "Signed Up")),
      ...notSigned.map((g) => buildRow(g, "Not Signed Up")),
    ];
    downloadCsv("guest-signup-status.csv", headers, rows);
  };

  const exportMostRequestedCsv = () => {
    const headers = ["Activity", "Requests", "Avg Rank", "Fulfilled", "Requesters (Choice #)"];
    const rows = activityRows.map((row) => {
      const detail = data?.activityDetails?.[row.activityId];
      const requesters = (detail?.requesters || []).map((g) => {
        const choiceLabel = g.rank ? `#${g.rank}` : "#-";
        return `${g.name || ""} (${choiceLabel})`;
      });
      return [
        row.name || "",
        row.count ?? 0,
        row.avgRank ?? "",
        row.fulfilled === true ? "Yes" : row.fulfilled === false ? "No" : "",
        requesters.join("; "),
      ];
    });
    downloadCsv("most-requested-activities.csv", headers, rows);
  };

  const selectedActivityRequesters = useMemo(() => {
    if (!selectedActivity) return [];
    return (selectedActivity.requesters || []).map((g) => {
      let rank = g.rank;
      if (!rank) {
        const choices = data?.guestChoices?.[g.id] || [];
        const match = choices.find((c) => c.id === selectedActivity.activityId);
        rank = match?.rank;
      }
      return { ...g, rank };
    });
  }, [data, selectedActivity]);


  return (
    <div style={{ maxWidth: 1000, margin: "0 auto", padding: 20 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12 }}>
        <div>
          <div style={{ fontWeight: 800, fontSize: 20 }}>Reports</div>
          <div style={{ fontSize: 12, opacity: 0.7 }}>
            Run built-in reports or filter results below.
          </div>
        </div>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <label style={{ fontSize: 12, fontWeight: 700 }}>Report</label>
          <select value={reportId} onChange={(e) => setReportId(e.target.value)}>
            {REPORTS.map((r) => (
              <option key={r.id} value={r.id}>
                {r.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      {reportId !== "builder" && (
        <div style={{ marginTop: 12, border: "1px solid var(--stroke)", borderRadius: 12, padding: 14, background: "var(--card)" }}>
          <div style={{ fontWeight: 800 }}>Report Builder</div>
          <div style={{ marginTop: 10, display: "flex", gap: 12, flexWrap: "wrap", alignItems: "center" }}>
            <label style={{ display: "flex", gap: 6, alignItems: "center" }}>
              <span style={{ fontSize: 12, fontWeight: 700 }}>Min requests</span>
              <input
                type="number"
                min="1"
                value={minRequests}
                onChange={(e) => setMinRequests(Number(e.target.value || 1))}
                style={{ width: 80 }}
              />
            </label>
            {reportId === "most_requested" || reportId === "unfulfilled" ? (
              <label style={{ display: "flex", gap: 6, alignItems: "center" }}>
                <input
                  type="checkbox"
                  checked={onlyUnfulfilled}
                  onChange={(e) => setOnlyUnfulfilled(e.target.checked)}
                />
                <span style={{ fontSize: 12, fontWeight: 700 }}>Only unfulfilled activities</span>
              </label>
            ) : null}
          </div>
        </div>
      )}

      {loading && <div style={{ marginTop: 16, opacity: 0.7 }}>Loading…</div>}
      {error && <div style={{ marginTop: 16, color: "crimson" }}>{error}</div>}

      {!loading && !error && data && reportId !== "builder" && (
        <div style={{ marginTop: 16, display: "grid", gap: 16 }}>
          {reportId === "signup" && (
            <div style={{ border: "1px solid var(--stroke)", borderRadius: 12, padding: 14, background: "var(--card)" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, marginBottom: 8 }}>
                <div style={{ fontWeight: 800 }}>Guest Signup Status</div>
                <button onClick={exportSignupCsv} style={{ fontSize: 12, padding: "6px 12px" }}>
                  Export CSV
                </button>
              </div>
              <div style={{ display: "flex", gap: 16, flexWrap: "wrap" }}>
                <div><strong>{data.signup?.signedUpCount || 0}</strong> signed up</div>
                <div><strong>{data.signup?.notSignedUpCount || 0}</strong> not signed up</div>
              </div>
              <div style={{ marginTop: 12, display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                <div>
                  <div style={{ fontWeight: 700, fontSize: 12, marginBottom: 6 }}>Signed Up</div>
                  <div style={{ display: "grid", gap: 4 }}>
                    {(data.signup?.signedUpGuests || []).map((g) => {
                      const isOpen = selectedGuestId === g.id;
                      const choices = data?.guestChoices?.[g.id] || [];
                      const rawGuest = rawGuestMap.get(g.id);
                      const skipSelections = rawGuest?.skipSelections || [];
                      const skipLabels = [0, 1, 2].map((idx) => {
                        for (const id of rawGuest?.familyIds || []) {
                          const fam = familyMap.get(id);
                          if (!fam) continue;
                          const text = fam[`skipText${idx + 1}`] || "";
                          if (text) return text;
                          const date = fam[`skipDate${idx + 1}`] || "";
                          const slot = fam[`skipSlot${idx + 1}`] || "";
                          if (date || slot) return [date, slot].filter(Boolean).join(" ");
                        }
                        return "";
                      });
                      return (
                      <div key={g.id} style={{ fontSize: 12 }}>
                        <button
                          onClick={() =>
                            setSelectedGuestId((prev) => (prev === g.id ? "" : g.id))
                          }
                          style={{ background: "transparent", color: "var(--ink)", boxShadow: "none", padding: 0 }}
                        >
                          {g.name}
                        </button>{" "}
                        <span style={{ opacity: 0.7 }}>
                          {g.requestedCount}/5
                        </span>
                        {isOpen && (
                          <div style={{ marginTop: 6, display: "grid", gap: 4 }}>
                            {choices.map((c) => (
                              <div
                                key={`${g.id}-${c.id}-${c.rank}`}
                                style={{ display: "flex", justifyContent: "space-between", gap: 8 }}
                              >
                                <div>
                                  {c.rank}. {c.name}
                                </div>
                                <span
                                  style={{
                                    fontWeight: 700,
                                    fontSize: 11,
                                    color: c.scheduled ? "#1b7f4a" : "#8a3b12",
                                  }}
                                >
                                  {c.scheduled ? "Scheduled" : "Not scheduled"}
                                </span>
                              </div>
                            ))}
                            {choices.length === 0 && (
                              <div style={{ fontSize: 12, opacity: 0.7 }}>No requests submitted.</div>
                            )}
                            {skipSelections.some(Boolean) && (
                              <div style={{ marginTop: 6, fontSize: 12, opacity: 0.7 }}>
                                {skipSelections
                                  .map((selected, idx) =>
                                    selected ? `Skip ${idx + 1}: ${skipLabels[idx] || "Yes"}` : null
                                  )
                                  .filter(Boolean)
                                  .join(" • ")}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    );
                    })}
                  </div>
                </div>
                <div>
                  <div style={{ fontWeight: 700, fontSize: 12, marginBottom: 6 }}>Not Signed Up</div>
                  <div style={{ display: "grid", gap: 4 }}>
                    {(data.signup?.notSignedUpGuests || []).map((g) => {
                      const isOpen = selectedGuestId === g.id;
                      const choices = data?.guestChoices?.[g.id] || [];
                      const rawGuest = rawGuestMap.get(g.id);
                      const skipSelections = rawGuest?.skipSelections || [];
                      const skipLabels = [0, 1, 2].map((idx) => {
                        for (const id of rawGuest?.familyIds || []) {
                          const fam = familyMap.get(id);
                          if (!fam) continue;
                          const text = fam[`skipText${idx + 1}`] || "";
                          if (text) return text;
                          const date = fam[`skipDate${idx + 1}`] || "";
                          const slot = fam[`skipSlot${idx + 1}`] || "";
                          if (date || slot) return [date, slot].filter(Boolean).join(" ");
                        }
                        return "";
                      });
                      return (
                      <div key={g.id} style={{ fontSize: 12 }}>
                        <button
                          onClick={() =>
                            setSelectedGuestId((prev) => (prev === g.id ? "" : g.id))
                          }
                          style={{ background: "transparent", color: "var(--ink)", boxShadow: "none", padding: 0 }}
                        >
                          {g.name}
                        </button>{" "}
                        <span style={{ opacity: 0.7 }}>
                          {g.requestedCount}/5
                        </span>
                        {isOpen && (
                          <div style={{ marginTop: 6, display: "grid", gap: 4 }}>
                            {choices.map((c) => (
                              <div
                                key={`${g.id}-${c.id}-${c.rank}`}
                                style={{ display: "flex", justifyContent: "space-between", gap: 8 }}
                              >
                                <div>
                                  {c.rank}. {c.name}
                                </div>
                                <span
                                  style={{
                                    fontWeight: 700,
                                    fontSize: 11,
                                    color: c.scheduled ? "#1b7f4a" : "#8a3b12",
                                  }}
                                >
                                  {c.scheduled ? "Scheduled" : "Not scheduled"}
                                </span>
                              </div>
                            ))}
                            {choices.length === 0 && (
                              <div style={{ fontSize: 12, opacity: 0.7 }}>No requests submitted.</div>
                            )}
                            {skipSelections.some(Boolean) && (
                              <div style={{ marginTop: 6, fontSize: 12, opacity: 0.7 }}>
                                {skipSelections
                                  .map((selected, idx) =>
                                    selected ? `Skip ${idx + 1}: ${skipLabels[idx] || "Yes"}` : null
                                  )
                                  .filter(Boolean)
                                  .join(" • ")}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    );
                    })}
                  </div>
                </div>
              </div>
            </div>
          )}

          {reportId === "most_requested" && (
            <div style={{ border: "1px solid var(--stroke)", borderRadius: 12, padding: 14, background: "var(--card)" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, marginBottom: 8 }}>
                <div style={{ fontWeight: 800 }}>Most Requested Activities</div>
                <button onClick={exportMostRequestedCsv} style={{ fontSize: 12, padding: "6px 12px" }}>
                  Export CSV
                </button>
              </div>
              <div style={{ display: "grid", gap: 6 }}>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 120px 140px", gap: 8, fontWeight: 700 }}>
                  <div>Activity</div>
                  <button
                    onClick={() =>
                      setMostRequestedSort((prev) => ({
                        key: "count",
                        dir: prev.key === "count" && prev.dir === "desc" ? "asc" : "desc",
                      }))
                    }
                    style={{ background: "transparent", color: "var(--ink)", boxShadow: "none", padding: 0, textAlign: "left" }}
                  >
                    Requests
                  </button>
                  <button
                    onClick={() =>
                      setMostRequestedSort((prev) => ({
                        key: "avgRank",
                        dir: prev.key === "avgRank" ? (prev.dir === "asc" ? "desc" : "asc") : "asc",
                      }))
                    }
                    style={{ background: "transparent", color: "var(--ink)", boxShadow: "none", padding: 0, textAlign: "left" }}
                  >
                    Avg Rank
                  </button>
                </div>
                {activityRows.map((row) => (
                  <div key={row.activityId} style={{ display: "grid", gridTemplateColumns: "1fr 120px 140px", gap: 8 }}>
                    <button
                      onClick={() => setSelectedActivityId(row.activityId)}
                      style={{ background: "transparent", color: "var(--ink)", boxShadow: "none", padding: 0, textAlign: "left" }}
                    >
                      {row.name}
                    </button>
                    <div style={{ fontWeight: 700 }}>{row.count}</div>
                    <div style={{ fontWeight: 700 }}>{row.avgRank ?? "-"}</div>
                  </div>
                ))}
                {activityRows.length === 0 && (
                  <div style={{ fontSize: 12, opacity: 0.7 }}>No activities match the filters.</div>
                )}
              </div>
            </div>
          )}

          {reportId === "unfulfilled" && (
            <div style={{ border: "1px solid var(--stroke)", borderRadius: 12, padding: 14, background: "var(--card)" }}>
              <div style={{ fontWeight: 800, marginBottom: 8 }}>Activities Not Able To Be Fulfilled</div>
              <div style={{ display: "grid", gap: 6 }}>
                {activityRows
                  .filter((row) => row.fulfilled === false)
                  .map((row) => (
                    <div key={row.activityId} style={{ display: "flex", justifyContent: "space-between", gap: 8 }}>
                      <button
                        onClick={() => setSelectedActivityId(row.activityId)}
                        style={{ background: "transparent", color: "var(--ink)", boxShadow: "none", padding: 0 }}
                      >
                        {row.name}
                      </button>
                      <div style={{ fontWeight: 700 }}>{row.count}</div>
                    </div>
                  ))}
                {activityRows.filter((row) => row.fulfilled === false).length === 0 && (
                  <div style={{ fontSize: 12, opacity: 0.7 }}>All requested activities have scheduled slots.</div>
                )}
              </div>
            </div>
          )}

          {reportId === "over_capacity" && (
            <div style={{ border: "1px solid var(--stroke)", borderRadius: 12, padding: 14, background: "var(--card)" }}>
              <div style={{ fontWeight: 800, marginBottom: 8 }}>Requested Over Capacity</div>
              <div style={{ display: "grid", gap: 6 }}>
                {overCapacityRows.map((row) => (
                  <div key={row.activityId} style={{ display: "flex", justifyContent: "space-between", gap: 8 }}>
                    <button
                      onClick={() => setSelectedActivityId(row.activityId)}
                      style={{ background: "transparent", color: "var(--ink)", boxShadow: "none", padding: 0 }}
                    >
                      {row.name}
                    </button>
                    <div style={{ fontWeight: 700 }}>
                      {row.requested}/{row.remaining}
                    </div>
                  </div>
                ))}
                {overCapacityRows.length === 0 && (
                  <div style={{ fontSize: 12, opacity: 0.7 }}>No activities over capacity.</div>
                )}
              </div>
            </div>
          )}

          {reportId === "opted_out" && (
            <div style={{ border: "1px solid var(--stroke)", borderRadius: 12, padding: 14, background: "var(--card)" }}>
              <div style={{ fontWeight: 800, marginBottom: 8 }}>Guests Opted Out of Activities</div>
              <div style={{ display: "grid", gap: 6 }}>
                {optedOutRows.map((g) => {
                  const labels = [0, 1, 2].map((idx) => {
                    for (const id of g.familyIds || []) {
                      const fam = familyMap.get(id);
                      if (!fam) continue;
                      const text = fam[`skipText${idx + 1}`] || "";
                      if (text) return text;
                      const date = fam[`skipDate${idx + 1}`] || "";
                      const slot = fam[`skipSlot${idx + 1}`] || "";
                      if (date || slot) return [date, slot].filter(Boolean).join(" ");
                    }
                    return "";
                  });
                  return (
                    <div key={g.id} style={{ fontSize: 12 }}>
                      <strong>{g.name}</strong>{" "}
                      <span style={{ opacity: 0.7 }}>
                        {g.skipSelections.map((selected, idx) =>
                          selected ? `Skip ${idx + 1}: ${labels[idx] || "Yes"}` : null
                        ).filter(Boolean).join(" • ")}
                      </span>
                    </div>
                  );
                })}
                {optedOutRows.length === 0 && (
                  <div style={{ fontSize: 12, opacity: 0.7 }}>No guests have opted out.</div>
                )}
              </div>
            </div>
          )}

          {selectedActivity && (
            <div style={{ border: "1px solid var(--stroke)", borderRadius: 12, padding: 14, background: "var(--card)" }}>
              <div style={{ fontWeight: 800, marginBottom: 6 }}>{selectedActivity.name}</div>
              <div style={{ fontSize: 12, opacity: 0.7, marginBottom: 10 }}>
                Requested: {selectedActivity.requested || 0} • Scheduled: {selectedActivity.scheduled || 0} • Capacity: {selectedActivity.capacity ?? 0}
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                <div>
                  <div style={{ fontWeight: 700, fontSize: 12, marginBottom: 6 }}>Requesters</div>
                  <div style={{ display: "grid", gap: 4 }}>
                    {selectedActivityRequesters
                      .slice()
                      .sort((a, b) => (a.rank ?? 999) - (b.rank ?? 999))
                      .map((g) => (
                      <div key={g.id} style={{ fontSize: 12 }}>
                        {g.name}{" "}
                        <span style={{ opacity: 0.7 }}>
                          (Choice #{g.rank || "-"})
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
                <div>
                  <div style={{ fontWeight: 700, fontSize: 12, marginBottom: 6 }}>Scheduled</div>
                  <div style={{ display: "grid", gap: 4 }}>
                    {(selectedActivity.scheduledGuests || []).map((g) => (
                      <div key={g.id} style={{ fontSize: 12 }}>{g.name}</div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {reportId === "builder" && (
      <div style={{ marginTop: 16, border: "1px solid var(--stroke)", borderRadius: 12, padding: 16, background: "var(--card)" }}>
        <div style={{ fontWeight: 800, marginBottom: 8 }}>Custom Report Builder</div>
        <div style={{ display: "flex", gap: 12, flexWrap: "wrap", alignItems: "center" }}>
          <label style={{ display: "flex", gap: 6, alignItems: "center" }}>
            <span style={{ fontSize: 12, fontWeight: 700 }}>Source</span>
            <select value={builderSource} onChange={(e) => setBuilderSource(e.target.value)}>
              <option value="guests">Guests</option>
              <option value="activities">Activities</option>
              <option value="schedules">Schedules</option>
            </select>
          </label>
          <label style={{ display: "flex", gap: 6, alignItems: "center" }}>
            <span style={{ fontSize: 12, fontWeight: 700 }}>Search</span>
            <input value={builderSearch} onChange={(e) => setBuilderSearch(e.target.value)} />
          </label>
          {(builderSource === "guests" || builderSource === "schedules" || builderSource === "activities") && (
            <>
              <label style={{ display: "flex", gap: 6, alignItems: "center" }}>
                <span style={{ fontSize: 12, fontWeight: 700 }}>Preset</span>
                <select value={builderDatePreset} onChange={(e) => setBuilderDatePreset(e.target.value)}>
                  <option value="today">Today</option>
                  <option value="tomorrow">Tomorrow</option>
                  <option value="hansen_wedding">Hansen Wedding (3/26-3/30)</option>
                  <option value="this_week">This week</option>
                  <option value="next_week">Next week</option>
                </select>
              </label>
              <label style={{ display: "flex", gap: 6, alignItems: "center" }}>
                <span style={{ fontSize: 12, fontWeight: 700 }}>From</span>
                <input type="date" value={builderFrom} onChange={(e) => setBuilderFrom(e.target.value)} />
              </label>
              <label style={{ display: "flex", gap: 6, alignItems: "center" }}>
                <span style={{ fontSize: 12, fontWeight: 700 }}>To</span>
                <input type="date" value={builderTo} onChange={(e) => setBuilderTo(e.target.value)} />
              </label>
            </>
          )}
          {(builderSource === "guests" || builderSource === "activities") && (
            <label style={{ display: "flex", gap: 6, alignItems: "center" }}>
              <span style={{ fontSize: 12, fontWeight: 700 }}>Min requests</span>
              <input
                type="number"
                min="0"
                value={builderMinRequested}
                onChange={(e) => setBuilderMinRequested(Number(e.target.value || 0))}
                style={{ width: 80 }}
              />
            </label>
          )}
          {builderSource === "guests" && (
            <label style={{ display: "flex", gap: 6, alignItems: "center" }}>
              <input
                type="checkbox"
                checked={builderHasRequests}
                onChange={(e) => setBuilderHasRequests(e.target.checked)}
              />
              <span style={{ fontSize: 12, fontWeight: 700 }}>Only with requests</span>
            </label>
          )}
        </div>

        <div style={{ marginTop: 12, display: "grid", gap: 8 }}>
          <div style={{ fontWeight: 700, fontSize: 12 }}>Columns</div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
            {(schemaBySource[builderSource] || []).map((col) => (
              <label key={col.key} style={{ display: "flex", gap: 6, alignItems: "center" }}>
                <input
                  type="checkbox"
                  checked={builderColumns.includes(col.key)}
                  onChange={(e) => {
                    setBuilderColumns((prev) =>
                      e.target.checked ? [...prev, col.key] : prev.filter((c) => c !== col.key)
                    );
                  }}
                />
                <span style={{ fontSize: 12 }}>{col.label}</span>
              </label>
            ))}
          </div>
        </div>

        <div style={{ marginTop: 12, display: "flex", gap: 12, flexWrap: "wrap", alignItems: "center" }}>
          <label style={{ display: "flex", gap: 6, alignItems: "center" }}>
            <span style={{ fontSize: 12, fontWeight: 700 }}>Group by</span>
            <select value={builderGroupBy} onChange={(e) => setBuilderGroupBy(e.target.value)}>
              <option value="">None</option>
              {(schemaBySource[builderSource] || []).map((col) => (
                <option key={col.key} value={col.key}>
                  {col.label}
                </option>
              ))}
            </select>
          </label>
          {builderGroupBy && (
            <>
              <label style={{ display: "flex", gap: 6, alignItems: "center" }}>
                <span style={{ fontSize: 12, fontWeight: 700 }}>Aggregate</span>
                <select value={builderAggregate} onChange={(e) => setBuilderAggregate(e.target.value)}>
                  <option value="count">Count</option>
                  <option value="sum">Sum</option>
                </select>
              </label>
              {builderAggregate === "sum" && (
                <label style={{ display: "flex", gap: 6, alignItems: "center" }}>
                  <span style={{ fontSize: 12, fontWeight: 700 }}>Sum field</span>
                  <select value={builderSumField} onChange={(e) => setBuilderSumField(e.target.value)}>
                    <option value="">Select field…</option>
                    {(schemaBySource[builderSource] || [])
                      .filter((col) => col.numeric)
                      .map((col) => (
                        <option key={col.key} value={col.key}>
                          {col.label}
                        </option>
                      ))}
                  </select>
                </label>
              )}
            </>
          )}
        </div>

        {rawLoading && <div style={{ marginTop: 12, fontSize: 12, opacity: 0.7 }}>Loading raw data…</div>}
        {rawError && <div style={{ marginTop: 12, fontSize: 12, color: "crimson" }}>{rawError}</div>}

        {!rawLoading && !rawError && (
          <div style={{ marginTop: 12, overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
              <thead>
                <tr style={{ textAlign: "left" }}>
                  {builderGroupBy ? (
                    <>
                      <th style={{ borderBottom: "1px solid var(--stroke)", padding: "6px 4px" }}>Group</th>
                      <th style={{ borderBottom: "1px solid var(--stroke)", padding: "6px 4px" }}>Count</th>
                      {builderAggregate === "sum" && builderSumField && (
                        <th style={{ borderBottom: "1px solid var(--stroke)", padding: "6px 4px" }}>Sum</th>
                      )}
                    </>
                  ) : (
                    builderColumns.map((col) => (
                      <th key={col} style={{ borderBottom: "1px solid var(--stroke)", padding: "6px 4px" }}>
                        {(schemaBySource[builderSource] || []).find((c) => c.key === col)?.label || col}
                      </th>
                    ))
                  )}
                </tr>
              </thead>
              <tbody>
                {(builderGroupBy ? builderOutput : builderRows).map((row) => (
                  <tr key={builderGroupBy ? row.group : row.id}>
                    {builderGroupBy ? (
                      <>
                        <td style={{ padding: "6px 4px", borderBottom: "1px solid var(--stroke)" }}>{row.group}</td>
                        <td style={{ padding: "6px 4px", borderBottom: "1px solid var(--stroke)" }}>{row.count}</td>
                        {builderAggregate === "sum" && builderSumField && (
                          <td style={{ padding: "6px 4px", borderBottom: "1px solid var(--stroke)" }}>{row.sum}</td>
                        )}
                      </>
                    ) : (
                      builderColumns.map((col) => (
                        <td key={`${row.id}-${col}`} style={{ padding: "6px 4px", borderBottom: "1px solid var(--stroke)" }}>
                          {row[col] ?? ""}
                        </td>
                      ))
                    )}
                  </tr>
                ))}
                {(builderGroupBy ? builderOutput : builderRows).length === 0 && (
                  <tr>
                    <td style={{ padding: "6px 4px", fontSize: 12, opacity: 0.7 }} colSpan={builderGroupBy ? 3 : builderColumns.length}>
                      No rows match these filters.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
      )}
    </div>
  );
}
